/**
 * 元素在 Page 与 workspaceElements 之间的归属判定（50% 面积阈值）。
 */
import type { Element, Page } from '@/components/canvas/document/publicTypes';
import type { WorkspacePageLayoutMode } from '@/components/canvas/workspace/model/workspacePageLayoutMode';
import { getSingleModeTopmostVisiblePageId } from '@/components/canvas/workspace/model/pageArrangement';
import { getElementAABB } from '@/components/canvas/workspace/controllers/marqueeSelectUtils';

export const OWNERSHIP_OVERLAP_THRESHOLD = 0.5;

export interface BoundsRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function overlapAreaRatio(element: BoundsRect, container: BoundsRect): number {
  const ix0 = Math.max(element.x, container.x);
  const iy0 = Math.max(element.y, container.y);
  const ix1 = Math.min(element.x + element.width, container.x + container.width);
  const iy1 = Math.min(element.y + element.height, container.y + container.height);
  if (ix1 <= ix0 || iy1 <= iy0) return 0;
  const intersection = (ix1 - ix0) * (iy1 - iy0);
  const area = element.width * element.height;
  return area > 0 ? intersection / area : 0;
}

/** 元素宽度落入 container 的水平占比（相对元素自身宽度） */
export function overlapWidthRatio(element: BoundsRect, container: BoundsRect): number {
  if (element.width <= 0) return 0;
  const ix0 = Math.max(element.x, container.x);
  const ix1 = Math.min(element.x + element.width, container.x + container.width);
  if (ix1 <= ix0) return 0;
  return (ix1 - ix0) / element.width;
}

/** 元素高度落入 container 的垂直占比（相对元素自身高度） */
export function overlapHeightRatio(element: BoundsRect, container: BoundsRect): number {
  if (element.height <= 0) return 0;
  const iy0 = Math.max(element.y, container.y);
  const iy1 = Math.min(element.y + element.height, container.y + container.height);
  if (iy1 <= iy0) return 0;
  return (iy1 - iy0) / element.height;
}

/**
 * 工作区元素是否应落入该页：联合包围盒与页板面的交集面积占元素面积 ≥50%。
 * 与页内拖出判定（`shouldMovePageElementsToWorkspace`）对称，避免仅用宽/高单轴 ≥50%
 * 时实际面积不足 50% 仍被吸进页面。
 */
export function shouldWorkspaceElementDropOnPage(
  worldBounds: BoundsRect,
  page: Pick<Page, 'workspaceLayout' | 'size'>,
): boolean {
  const surface = pageSurfaceWorldRect(page);
  return overlapAreaRatio(worldBounds, surface) >= OWNERSHIP_OVERLAP_THRESHOLD;
}

export function pageSurfaceWorldRect(page: Pick<Page, 'workspaceLayout' | 'size'>): BoundsRect {
  const { x, y } = page.workspaceLayout;
  return { x, y, width: page.size.width, height: page.size.height };
}

export function pageLocalRect(page: Pick<Page, 'size'>): BoundsRect {
  return { x: 0, y: 0, width: page.size.width, height: page.size.height };
}

/** 多选拖拽联合包围盒（positions 与 elements 同坐标系） */
export function unionBoundsFromDrag(
  startPositions: { id: string; x: number; y: number }[],
  elements: Element[],
  dx: number,
  dy: number,
): BoundsRect | null {
  if (startPositions.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { id, x, y } of startPositions) {
    const el = elements.find((e) => e.id === id);
    const { w, h } = el ? getElementAABB(el) : { w: 0, h: 0 };
    const px = x + dx;
    const py = y + dy;
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px + w);
    maxY = Math.max(maxY, py + h);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;
  return { x: minX, y: minY, width, height };
}

/** 页内坐标 → 世界坐标 */
export function pageLocalBoundsToWorld(bounds: BoundsRect, page: Pick<Page, 'workspaceLayout'>): BoundsRect {
  const { x: ox, y: oy } = page.workspaceLayout;
  return {
    x: bounds.x + ox,
    y: bounds.y + oy,
    width: bounds.width,
    height: bounds.height,
  };
}

/** 世界坐标 → 页内坐标 */
export function worldBoundsToPageLocal(bounds: BoundsRect, page: Pick<Page, 'workspaceLayout'>): BoundsRect {
  const { x: ox, y: oy } = page.workspaceLayout;
  return {
    x: bounds.x - ox,
    y: bounds.y - oy,
    width: bounds.width,
    height: bounds.height,
  };
}

/** 世界包围盒是否完全落在容器内（用于面板拖入图片等「整图在页内」判定） */
export function isBoundsFullyInside(inner: BoundsRect, outer: BoundsRect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

/** 完全落在某页内容区内的页面（zIndex 高者优先） */
export function findPageFullyContainingBounds(
  pages: Page[],
  worldBounds: BoundsRect,
): Page | null {
  const sorted = [...pages].sort(
    (a, b) => (b.workspaceLayout.zIndex ?? 0) - (a.workspaceLayout.zIndex ?? 0),
  );
  for (const page of sorted) {
    if (isBoundsFullyInside(worldBounds, pageSurfaceWorldRect(page))) {
      return page;
    }
  }
  return null;
}

/** 工作区元素拖入：返回满足面积阈值的页面（取面积占比最大者） */
export function findWorkspaceDropTargetPage(
  pages: Page[],
  worldBounds: BoundsRect,
): string | null {
  let bestId: string | null = null;
  let bestScore = -1;
  for (const page of pages) {
    const surface = pageSurfaceWorldRect(page);
    const score = overlapAreaRatio(worldBounds, surface);
    if (score < OWNERSHIP_OVERLAP_THRESHOLD) continue;
    if (score > bestScore) {
      bestScore = score;
      bestId = page.id;
    }
  }
  return bestId;
}

/** 页内元素跨页拖入：在排除源页后取面积占比最大的目标页（源页仍 ≥50% 时不得占掉落页高亮） */
export function findPageCrossDropTargetPage(
  pages: Page[],
  worldBounds: BoundsRect,
  sourcePageId: string,
): string | null {
  let bestId: string | null = null;
  let bestScore = -1;
  for (const page of pages) {
    if (page.id === sourcePageId) continue;
    const surface = pageSurfaceWorldRect(page);
    const score = overlapAreaRatio(worldBounds, surface);
    if (score < OWNERSHIP_OVERLAP_THRESHOLD) continue;
    if (score > bestScore) {
      bestScore = score;
      bestId = page.id;
    }
  }
  return bestId;
}

/**
 * 按布局模式解析工作区元素落页目标。
 * 单页模式各页叠在同一坐标：仅当包围盒与「可见页中最上层」页板面 ≥50% 时落页（与渲染叠放一致）。
 */
export function resolveWorkspaceDropTargetPage(
  layoutMode: WorkspacePageLayoutMode,
  pages: Page[],
  currentPageId: string,
  worldBounds: BoundsRect,
  draggingPageId: string | null = null,
): string | null {
  if (layoutMode === 'single') {
    const topId = getSingleModeTopmostVisiblePageId(pages, currentPageId, draggingPageId);
    if (!topId) return null;
    const page = pages.find((p) => p.id === topId);
    if (!page || !shouldWorkspaceElementDropOnPage(worldBounds, page)) return null;
    return topId;
  }
  return findWorkspaceDropTargetPage(pages, worldBounds);
}

/**
 * 按布局模式解析页内元素跨页落点。
 * 单页模式禁止页间归属迁移，仅支持页 ↔ 工作区。
 */
export function resolvePageCrossDropTargetPage(
  layoutMode: WorkspacePageLayoutMode,
  pages: Page[],
  worldBounds: BoundsRect,
  sourcePageId: string,
): string | null {
  if (layoutMode === 'single') return null;
  return findPageCrossDropTargetPage(pages, worldBounds, sourcePageId);
}

/** 页内元素拖出：是否应归入 workspace（页内面积占比 < 50%） */
export function shouldMovePageElementsToWorkspace(
  page: Pick<Page, 'size'>,
  localBounds: BoundsRect,
): boolean {
  return overlapAreaRatio(localBounds, pageLocalRect(page)) < OWNERSHIP_OVERLAP_THRESHOLD;
}
